import type React from "react";

import { faArrowDownRight, faArrowsMaximize, faSidebarFlip } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { VideoCallMode } from "../types";

import { PopoverCloseButton, PopoverContainer, PopoverTitle } from "./PopoverStyles";

interface Props {
  currentMode: VideoCallMode;
  onModeChange: (mode: VideoCallMode) => void;
  onClose: () => void;
}

export const ViewModeSelector: React.FC<Props> = ({ currentMode, onModeChange, onClose }) => {
  const { t } = useTranslation();

  return (
    <PopoverContainer>
      <PopoverCloseButton onClick={onClose}>&times;</PopoverCloseButton>
      <PopoverTitle>{t("videocall.view_mode.title")}</PopoverTitle>
      <OptionsContainer>
        <ModeOption $isActive={currentMode === "docked"} onClick={() => onModeChange("docked")}>
          <ModeIcon icon={faSidebarFlip} $isActive={currentMode === "docked"} />
          <ModeLabel>{t("videocall.view_mode.docked")}</ModeLabel>
        </ModeOption>
        <ModeOption $isActive={currentMode === "floating"} onClick={() => onModeChange("floating")}>
          <ModeIcon icon={faArrowsMaximize} $isActive={currentMode === "floating"} />
          <ModeLabel>{t("videocall.view_mode.floating")}</ModeLabel>
        </ModeOption>
        <ModeOption $isActive={currentMode === "minimised"} onClick={() => onModeChange("minimised")}>
          <ModeIcon icon={faArrowDownRight} $isActive={currentMode === "minimised"} />
          <ModeLabel>{t("videocall.view_mode.minimised")}</ModeLabel>
        </ModeOption>
      </OptionsContainer>
    </PopoverContainer>
  );
};

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ModeOption = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: ${({ $isActive }) => ($isActive ? "rgba(255, 255, 255, 0.15)" : "transparent")};
  color: white;
  cursor: pointer;
  transition: background 0.15s;
  width: 100%;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ModeIcon = styled(FontAwesomeIcon)<{ $isActive: boolean }>`
  width: 16px;
  height: 16px;
  color: ${({ $isActive }) => ($isActive ? "white" : "rgba(255, 255, 255, 0.6)")};
`;

const ModeLabel = styled.span`
  font-size: 14px;
`;
